import { constructNow, differenceInDays, differenceInHours, parseISO } from 'date-fns';

import { CartState } from '../cart/reducer';
import { selectPromoFreeItemCount } from '../cart/selectors';
import ooeConstants from '../constants';
import { selectLeadTimeWarning } from '../eventDetails/selectors';
import {
  selectDeliveryLeadTimeStoreInfo,
  selectInStateDeliveryOnly,
  selectMaxDeliveryAmount,
  selectMaxPickupAmount,
  selectMaxSameDayOrderTotalAmountCents,
  selectMinDeliveryAmount,
  selectMinPickupAmount,
  selectPickupLeadTimeStoreInfo,
} from '../floatingMenu/selectors';
import { State as GuestState } from '../guest/reducer';
import {
  selectCmtCustomerCFAId,
  selectFavoriteOrders,
  selectIsCmtCustomerSelected,
  selectPastOrdersLength,
  selectVaultedCards,
} from '../guest/selectors';
import { State as MenuState } from '../menu/reducer';
import type { State as OrderState } from '../order/reducers';
import {
  selectDeliveryAddressValid,
  selectDeliveryRangeWarning,
  selectDeliveryTipAmount,
  selectDeliveryTipPercentOfSale,
  selectDestination,
  selectDestinationValid,
  selectEditMode,
  selectEditOrderId,
  selectNewPaymentMethod,
  selectOverrideGuestDetails,
  selectReorder,
  selectSelectedPaymentMethod,
  selectSubTotalAmount,
  selectTaxAmount,
  selectTransactionId,
} from '../order/selectors';
import {
  selectDateIsToday,
  selectDeliveryMinNotMet,
  selectExceedsMaxOrderTotal,
  selectExceedsSameDayMaxOrderTotal,
  selectOrderForAPI,
  selectPickupMinNotMet,
  selectPromoFreeItemsValue,
  selectSubtotalSubmitWarning,
} from '../shared/selectors';
import type { State as UserState } from '../user/reducer';
import {
  doesLocationNumberExist,
  isAdminUser,
  isVcaUser,
  selectBypassBusinessRules,
  selectCmtPerms,
  selectDeliveryRange,
  selectIsDeveloperAudience,
  selectLocationAcceptsDelivery,
  selectLocationAcceptsPickup,
  selectLocationNumber,
  selectLocationTimezone,
  selectOrderMaxFutureDayCountDelivery,
  selectOrderMaxFutureDayCountPickup,
  selectUser,
  selectUserCfaGuid,
  selectUserEmail,
  selectUserHasAgreedToEula,
  selectUserName,
  selectUserType,
} from '../user/selectors';
import ReduxStore from '../util/reduxStore';
import { roundNumber } from '../util/utils';
import { logAmplitudeEvent, setAmplitudeUser } from './amplitude';

export const createSubmitOrderAnalytics = (state: {
  cart: CartState;
  guest: GuestState;
  menu: MenuState;
  order: OrderState;
  user: UserState;
}) => {
  const order = selectOrderForAPI(state);
  const user = selectUser(state);
  const selectedPaymentMethod = selectSelectedPaymentMethod(state);

  const now = constructNow(new Date());
  const orderDate = parseISO(order.promiseDateTime || '');

  return {
    // top level selectors
    deliveryMinNotMet: selectDeliveryMinNotMet(state),
    pickupMinNotMet: selectPickupMinNotMet(state),
    exceedsMaxOrderTotal: selectExceedsMaxOrderTotal(state),
    exceedsSameDayMaxOrderTotal: selectExceedsSameDayMaxOrderTotal(state),
    subtotalSubmitWarning: selectSubtotalSubmitWarning(state),
    leadTimeWarning: selectLeadTimeWarning(state),

    // order
    requestingNewPaymentMethod: selectNewPaymentMethod(state),
    isCmtCustomerSelected: selectIsCmtCustomerSelected(state),
    isEditingOrder: selectEditMode(state),
    editOrderId: selectEditOrderId(state),
    guestDetailsChanged: selectOverrideGuestDetails(state),
    orderId: order.id,
    companyName: order.companyName,
    destination: selectDestination(state),
    deliveryAddressValid: selectDeliveryAddressValid(state),
    destinationValid: selectDestinationValid(state),
    taxAmount: selectTaxAmount(state),
    subTotalAmount: selectSubTotalAmount(state),
    totalAmount: roundNumber(
      selectTaxAmount(state) + selectSubTotalAmount(state) + selectDeliveryTipAmount(state),
    ),
    deliveryRangeWarning: selectDeliveryRangeWarning(state),
    reorder: selectReorder(state),
    promiseDateTime: order.promiseDateTime,
    hoursUntilPromisedDateTime: roundNumber(differenceInHours(orderDate, now)),
    daysUntilPromisedDateTime: roundNumber(differenceInDays(orderDate, now)),
    paperGoods: order.paperGoods,
    specialInstructions: order.specialInstructions,
    guestCount: order.guestCount,
    businessDate: order.businessDate,
    orderLocationNumber: order.locationNumber,
    bypassBusinessRulesList: order.bypassBusinessRulesList,
    orderType: order.orderType,
    deliveryTipAmount: selectDeliveryTipAmount(state),
    deliveryTipPercentOfSale: selectDeliveryTipPercentOfSale(state),
    sendCustomerNotifications: order.sendCustomerNotifications,
    transactionId: selectTransactionId(state),

    //cart
    promoFreeItemCount: selectPromoFreeItemCount(state),
    promoFreeItemsValue: selectPromoFreeItemsValue(state),

    // user
    locationNumber: selectLocationNumber(state),
    bypassBusinessRules: selectBypassBusinessRules(state),
    cmtPerm: selectUserType(state),
    perms: selectCmtPerms(state),
    userCfaGuid: selectUserCfaGuid(state),
    audience: user.cfaAud,
    isAdmin: isAdminUser(state),
    isVca: isVcaUser(state),
    isDeveloper: selectIsDeveloperAudience(state),
    locationTimezone: selectLocationTimezone(state),
    userHasAgreedToEula: selectUserHasAgreedToEula(state),
    locationNumberExists: doesLocationNumberExist(state, order.locationNumber),
    locationAcceptsDelivery: selectLocationAcceptsDelivery(state),
    locationAcceptsPickup: selectLocationAcceptsPickup(state),
    minDeliveryAmount: selectMinDeliveryAmount(state) || 0 / 100,
    minPickupAmount: selectMinPickupAmount(state) || 0 / 100,
    maxDeliveryAmount: selectMaxDeliveryAmount(state) || 0 / 100,
    maxPickupAmount: selectMaxPickupAmount(state) || 0 / 100,
    maxSameDayOrderTotalAmountCents: selectMaxSameDayOrderTotalAmountCents(state) || 0 / 100,
    orderMaxFutureDayCountDelivery: selectOrderMaxFutureDayCountDelivery(state),
    orderMaxFutureDayCountPickup: selectOrderMaxFutureDayCountPickup(state),
    deliveryRange: selectDeliveryRange(state),
    deliveryLeadTimeMinutes: selectDeliveryLeadTimeStoreInfo(state),
    pickupLeadTimeMinutes: selectPickupLeadTimeStoreInfo(state),
    inStateDeliveryOnly: selectInStateDeliveryOnly(state),

    //form
    orderedSameDay: selectDateIsToday(state),
    paymentMethod: selectedPaymentMethod,

    //guest
    favoriteOrdersCount: selectFavoriteOrders(state)?.length,
    pastOrdersCount: selectPastOrdersLength(state),
    cmtCustomerId: selectCmtCustomerCFAId(state),
    vaultedCardsCount: selectVaultedCards(state)?.length || 0,
    error: undefined,
  };
};

const getUserInfo = () => {
  const { state } = ReduxStore;
  if (!state.user) {
    return {
      isVca: false,
      isAdmin: false,
      userCfaGuid: '',
      cmtPerms: '',
      name: '',
      email: '',
      bypassBusinessRules: '',
      locationNumber: '',
      appVersion: ooeConstants.APP_VERSION,
    };
  }
  return {
    isVca: isVcaUser(state) ?? false,
    isAdmin: isAdminUser(state) ?? false,
    userCfaGuid: selectUserCfaGuid(state) ?? '',
    cmtPerms: selectCmtPerms(state) ?? '',
    name: selectUserName(state) ?? '',
    email: selectUserEmail(state) ?? '',
    bypassBusinessRules: selectBypassBusinessRules(state) ?? '',
    locationNumber: selectLocationNumber(state) ?? '',
    appVersion: ooeConstants.APP_VERSION,
  };
};

export function logEvent(event: string, payload?: object) {
  const userInfo = getUserInfo();
  logAmplitudeEvent(event, { ...payload, ...userInfo });
}

export function simplifyPage(page = '') {
  if (page?.startsWith('more/')) {
    return page.substring(4);
  }
  return page;
}
export function logPageView(page: string) {
  const userInfo = getUserInfo();
  const simplifiedPage = simplifyPage(page);
  logAmplitudeEvent(`[View Page] ${simplifiedPage}`, {
    page: simplifiedPage,
    ...userInfo,
  });
}

export function setUserLocation(locationNumber: string) {
  const userInfo = getUserInfo();
  logAmplitudeEvent('[Set User Location]', { ...userInfo, locationNumber });
}

export function setUser(cfaGuid: string) {
  const userInfo = getUserInfo();
  setAmplitudeUser(cfaGuid, userInfo);
}
